/**
 * Brand catalog: the ONE place brand values live for the SPA runtime.
 *
 * Plain data with no `import.meta` access, so Node-side build code
 * (vite.config.js's index.html transform, scripts/prerender-shells.mjs) can
 * import it too. Browser code should import `BRAND` from './index.js', which
 * picks the entry for this build's VITE_BRAND.
 *
 * mode:
 *   'agency'  — the full utopiancreations.one site (default; VITE_BRAND unset).
 *   'product' — the standalone product SPA (app.hollerpost.com): auth,
 *               dashboard, legal pages, nothing else.
 *
 * The worker keeps its own BRANDS map (worker/marketing-api.js) for
 * server-side strings; keep supportEmail/siteUrl in sync with it.
 */
import { freeTerms } from './plans.js';
export const BRANDS = {
  utopian: {
    slug: 'utopian',
    name: 'Utopian Creations',
    tagline: 'Websites, apps & AI for small businesses',
    logo: { type: 'text', text: 'Utopian Creations' },
    colors: {
      primary: '#7c3aed',
      primaryHover: '#6d28d9',
      onPrimary: '#ffffff',
      accentText: '#7c3aed',
      ink: '#1f2937',
      surface: '#f9fafb',
      success: '#059669',
    },
    supportEmail: 'support@utopiancreations.one',
    siteUrl: 'https://utopiancreations.one',
    appOrigin: 'https://utopiancreations.one',
    mode: 'agency',
  },
  hollerpost: {
    slug: 'hollerpost',
    name: 'Hollerpost',
    tagline: 'Growing your list shouldn’t grow your bill.',
    descriptor: 'Email and texts for small business, built and backed by a real person.',
    legalLine: 'Hollerpost is a service of Utopian Creations LLC.',
    // Wordmark: Nugget's head + lowercase "hollerpost" in Nunito 900
    // (Hollerpost design system). The SVGs live in public/brand/hollerpost/
    // and are used as-is: Nugget is never recolored or redrawn.
    logo: { type: 'text', text: 'hollerpost', mark: '/brand/hollerpost/nugget-head.svg' },
    // Design-system tokens. Burnt orange is the ONLY action color: white on it
    // is 5.18:1 and it reads 4.84:1 as text on cream. Buttons go solid ink on
    // hover. Nugget gold (#D9963F) is illustration-only, so it isn't here.
    colors: {
      primary: '#C2410C',
      primaryHover: '#2B211B',
      onPrimary: '#FFFFFF',
      accentText: '#C2410C',
      ink: '#2B211B',
      surface: '#FFF6EC',
      success: '#177A53',
      band: '#FBE8D3',
      muted: '#6B5A4E',
      line: '#E9D8C4',
    },
    // Product-only <head> assets, added by the brandHtml() plugin.
    assets: {
      favicon: '/brand/hollerpost/favicon.svg',
      stylesheet: '/brand/hollerpost/hollerpost.css',
      // Body is Nunito Sans (plain zero; Atkinson's slashed zero read like a
      // glitch in prices and dates). Nunito stays the display face.
      fonts: 'https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,opsz,wght@0,6..12,400;0,6..12,700;1,6..12,400&family=Nunito:wght@700;800;900&display=swap',
      nuggetBase: '/brand/hollerpost/nugget-',
    },
    supportEmail: 'support@hollerpost.com',
    siteUrl: 'https://hollerpost.com',
    appOrigin: 'https://app.hollerpost.com',
    mode: 'product',
    // SMS_STATUS: 'rolling_out' | 'live'. The one switch for every texting
    // mention in the app and (via scripts/export-hollerpost-plans.mjs →
    // hollerpost.plans.json) on hollerpost.com. Flip to 'live' when Telnyx
    // toll-free verification clears, then re-run the export + site deploy.
    smsStatus: 'rolling_out',
    // "1,000 emails a month" (the Free plan), from the generated plan catalog.
    freeLine: freeTerms(),
    // index.html <head> overrides, applied at build time by the
    // brandHtml() plugin in vite.config.js.
    meta: {
      title: 'Hollerpost: email and texts for small business',
      description:
        'Growing your list shouldn’t grow your bill. Fair-priced email and texting for small business, ' +
        `priced by what you send, not how many contacts you have. Free for ${freeTerms()}, no card.`,
      themeColor: '#FFF6EC',
    },
  },
};

export const DEFAULT_BRAND_SLUG = 'utopian';

export const brandForSlug = (slug) => BRANDS[slug] || BRANDS[DEFAULT_BRAND_SLUG];
